import React, { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { usePostHog } from 'posthog-js/react';
import { updateName, useSession } from '../lib/auth-client';
import DisplayNamePrompt from '../components/DisplayNamePrompt';
import './login.css';

const AuthCallback: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('returnTo') || '/';
  const posthog = usePostHog();
  const hasTrackedSuccess = useRef(false);
  const hasTrackedFailure = useRef(false);
  const { data: session, isPending, error, refetch, isRefetching } = useSession();
  const displayName = typeof session?.user?.name === 'string' ? session.user.name.trim() : '';
  const needsDisplayName = Boolean(session?.user && !displayName);
  const errorMessage = error?.message?.toLowerCase() || '';
  const isExpired = errorMessage.includes('expired');

  // Redirect to returnTo on successful auth
  useEffect(() => {
    if (session?.user && !isPending && !needsDisplayName) {
      // Brief delay to show success message
      const timer = setTimeout(() => {
        navigate(returnTo);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [session, isPending, navigate, returnTo, needsDisplayName]);

  useEffect(() => {
    if (!posthog || hasTrackedSuccess.current) return;

    if (session?.user && !isPending) {
      posthog.capture('auth_magic_link_verified', {
        route: returnTo || '/',
        is_new_user: !session.user.name
      });
      hasTrackedSuccess.current = true;
    }
  }, [posthog, session, isPending, returnTo]);

  useEffect(() => {
    if (!posthog || hasTrackedFailure.current) return;

    if (!isPending && (error || !session?.user)) {
      posthog.capture('auth_login_failed', {
        route: returnTo || '/',
        reason: isExpired ? 'expired' : 'invalid'
      });
      hasTrackedFailure.current = true;
    }
  }, [posthog, isPending, error, session, returnTo, isExpired]);

  const handleSaveName = async (name: string) => {
    await updateName(name);
    await refetch({ query: { disableCookieCache: true } });
  };

  if (isPending) {
    return (
      <div className="login">
        <main className="login__container">
          <h1 className="login__title">VERIFYING...</h1>
          <p className="login__message">Please wait while we verify your login link.</p>
        </main>
      </div>
    );
  }

  if (error || !session?.user) {
    // Retrieve stored email for "request new link" flow
    const storedEmail = sessionStorage.getItem('magicLinkEmail');
    
    // Determine error type - check if token is expired vs invalid/used
    const handleRequestNewLink = () => {
      // Preserve returnTo when requesting new link
      if (storedEmail) {
        // Pre-fill email if available
        navigate(`/login?returnTo=${encodeURIComponent(returnTo)}`);
      } else {
        navigate(`/login?returnTo=${encodeURIComponent(returnTo)}`);
      }
    };
    
    return (
      <div className="login">
        <main className="login__container">
          <h1 className="login__title">
            {isExpired ? 'LINK EXPIRED' : 'INVALID LINK'}
          </h1>
          <p className="login__message">
            {isExpired 
              ? 'This login link has expired. Links are valid for 15 minutes.'
              : 'This login link is invalid or has already been used.'}
          </p>
          <button className="login__button" onClick={handleRequestNewLink}>
            Request New Link
          </button>
        </main>
      </div>
    );
  }

  return (
    <div className="login">
      <main className="login__container">
        <h1 className="login__title">SUCCESS!</h1>
        <p className="login__message">
          You're now logged in as <strong>{session.user.email}</strong>
        </p>
        <p className="login__hint">
          {needsDisplayName ? 'Please set your display name to continue.' : 'Redirecting you to your page...'}
        </p>
      </main>
      <DisplayNamePrompt isOpen={needsDisplayName} onSave={handleSaveName} />
      {isRefetching ? <p className="login__hint">Updating your profile...</p> : null}
    </div>
  );
};

export default AuthCallback;
